import React, { useState, useEffect } from 'react';
import { getTournaments, createTournament, updateTournament, createRounds, updateRound, getRounds,
         getTournamentTemplates, cloneTournament } from '../services/api';

const DEFAULT_ROUNDS = [
    { round_number: 1, round_name: 'First Round',  game_count: 32, pick_deadline: '' },
    { round_number: 2, round_name: 'Second Round', game_count: 16, pick_deadline: '' },
    { round_number: 3, round_name: 'Sweet 16',     game_count: 8,  pick_deadline: '' },
    { round_number: 4, round_name: 'Elite 8',      game_count: 4,  pick_deadline: '' },
    { round_number: 5, round_name: 'Final Four',   game_count: 2,  pick_deadline: '' },
    { round_number: 6, round_name: 'Championship', game_count: 1,  pick_deadline: '' },
];

// A <input type="datetime-local"> gives/wants a naive 'YYYY-MM-DDTHH:mm'
// string with NO timezone marker. The browser itself always treats that
// naive string as the viewer's own local time. These two helpers make
// the frontend do the same, explicitly, in both directions, rather than
// relying on whatever timezone the database session happens to default
// to (which is how this bug happened: a naive string sent as-is
// round-tripped correctly against a local dev Postgres whose session
// timezone happened to match the developer's own, but the same naive
// string is genuinely ambiguous, and Railway's Postgres defaults to
// UTC -- a real, several-hour skew in the ACTUAL stored instant, not
// just a display glitch, since pick deadlines are enforced by comparing
// against the real clock).
function toLocalInputValue(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toUtcIso(localInputValue) {
    if (!localInputValue) return null;
    return new Date(localInputValue).toISOString();
}

function AdminSeasonSetup({ user, showFeedback, activeTournament, onTournamentCreated, onTournamentUpdated }) {
    const [tournaments,   setTournaments]   = useState([]);
    const [rounds,        setRounds]        = useState([]);
    const [roundEdits,    setRoundEdits]    = useState({});
    const [newName,       setNewName]       = useState('');
    const [newYear,       setNewYear]       = useState(new Date().getFullYear() + 1);
    const [newGender,     setNewGender]     = useState('M');
    const [newTotalTeams, setNewTotalTeams] = useState(64);
    const [roundInputs,   setRoundInputs]   = useState(DEFAULT_ROUNDS);
    const [section,       setSection]       = useState('tournament');
    const [editTournId,   setEditTournId]   = useState(null);
    const [editTournData, setEditTournData] = useState({});

    // Clone-from-template state
    const [createMode,     setCreateMode]     = useState('blank');    // 'blank' | 'template'
    const [templates,      setTemplates]      = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [cloning,        setCloning]        = useState(false);

    useEffect(() => {
        getTournaments().then(res => setTournaments(res.data));
        getTournamentTemplates().then(res => {
            setTemplates(res.data);
            if (res.data.length > 0) setSelectedTemplate(String(res.data[0].id));
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!activeTournament) return;
        getRounds(activeTournament.id).then(res => {
            setRounds(res.data);
            const edits = {};
            res.data.forEach(r => {
                edits[r.id] = {
                    pick_deadline: r.pick_deadline ? toLocalInputValue(r.pick_deadline) : '',
                    pick_group:    r.pick_group || ''
                };
            });
            setRoundEdits(edits);
        });
    }, [activeTournament]);

    const handleCreateTournament = async (e) => {
        e.preventDefault();
        try {
            const res = await createTournament({
                name: newName,
                year: newYear,
                gender: newGender,
                total_teams: newTotalTeams
            });
            showFeedback('Tournament created successfully');
            setNewName('');
            const updated = await getTournaments();
            setTournaments(updated.data);
            onTournamentCreated(res.data);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to create tournament', 'error');
        }
    };

    const handleCloneTournament = async (e) => {
        e.preventDefault();
        if (!selectedTemplate) return;
        setCloning(true);
        try {
            const res = await cloneTournament(parseInt(selectedTemplate), {
                name:   newName,
                year:   newYear,
                gender: newGender,
            });
            showFeedback('Tournament cloned successfully — teams, rounds, and games are ready');
            setNewName('');
            const updated = await getTournaments();
            setTournaments(updated.data);
            onTournamentCreated(res.data);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to clone tournament', 'error');
        } finally {
            setCloning(false);
        }
    };

    const handleSaveTournament = async (id) => {
        try {
            const res = await updateTournament(id, editTournData);
            showFeedback('Tournament updated');
            setEditTournId(null);
            const updated = await getTournaments();
            setTournaments(updated.data);
            // Propagate to Admin.js's top-level state too -- otherwise the
            // TOURNAMENT dropdown (a separate array there) keeps showing
            // the old name until a full page refresh.
            onTournamentUpdated?.(res.data);
        } catch (err) {
            showFeedback('Failed to update tournament', 'error');
        }
    };

    const handleCreateRounds = async () => {
        try {
            const rounds = roundInputs.map(r => ({
                ...r,
                pick_deadline: r.pick_deadline ? toUtcIso(r.pick_deadline) : null
            }));
            await createRounds(activeTournament.id, rounds);
            showFeedback('Rounds created successfully');
            const updated = await getRounds(activeTournament.id);
            setRounds(updated.data);
            const edits = {};
            updated.data.forEach(r => {
                edits[r.id] = {
                    pick_deadline: r.pick_deadline ? toLocalInputValue(r.pick_deadline) : '',
                    pick_group:    r.pick_group || ''
                };
            });
            setRoundEdits(edits);
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to create rounds', 'error');
        }
    };

    const handleUpdateDeadline = async (round) => {
        try {
            const deadline = roundEdits[round.id]?.pick_deadline;
            await updateRound(round.id, {
                pick_deadline: deadline ? toUtcIso(deadline) : null
            });
            showFeedback('Deadline updated');
        } catch (err) {
            showFeedback('Failed to update deadline', 'error');
        }
    };

    const handleUpdatePickGroup = async (round) => {
        try {
            const pickGroup = roundEdits[round.id]?.pick_group;
            await updateRound(round.id, {
                pick_group: pickGroup ? parseInt(pickGroup) : null
            });
            showFeedback('Pick group updated');
        } catch (err) {
            showFeedback('Failed to update pick group', 'error');
        }
    };

    const sections = [
        { id: 'tournament', label: 'Tournament' },
        { id: 'rounds',     label: 'Rounds & Deadlines' },
    ];

    return (
        <div>
            <div className="flex gap-2 mb-6">
                {sections.map(s => (
                    <button key={s.id} onClick={() => setSection(s.id)}
                        className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                            (section === s.id
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:text-white')}>
                        {s.label}
                    </button>
                ))}
            </div>

            {section === 'tournament' && (
                <div className="space-y-6">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                        <h3 className="text-lg font-black text-white mb-4">Create new tournament</h3>

                        {/* Blank vs Template toggle */}
                        <div className="flex gap-2 mb-5">
                            <button
                                type="button"
                                onClick={() => setCreateMode('blank')}
                                className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                                    (createMode === 'blank'
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-gray-800 text-gray-400 hover:text-white')}>
                                Blank
                            </button>
                            <button
                                type="button"
                                onClick={() => setCreateMode('template')}
                                className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                                    (createMode === 'template'
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-gray-800 text-gray-400 hover:text-white')}>
                                From template
                            </button>
                        </div>

                        {createMode === 'template' && (
                            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 mb-4 text-sm text-gray-400">
                                Clones an official bracket template — all 64 teams, 6 rounds, and games are copied automatically. You just set deadlines afterwards.
                            </div>
                        )}

                        <form
                            onSubmit={createMode === 'template' ? handleCloneTournament : handleCreateTournament}
                            className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                            {/* Template picker — only shown in template mode */}
                            {createMode === 'template' && (
                                <div className="sm:col-span-2">
                                    <label className="block text-xs text-gray-400 mb-1">Template</label>
                                    <select
                                        value={selectedTemplate}
                                        onChange={e => {
                                            setSelectedTemplate(e.target.value);
                                            const t = templates.find(t => String(t.id) === e.target.value);
                                            if (t) setNewGender(t.gender);
                                        }}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                        {templates.length === 0
                                            ? <option value="">No templates available</option>
                                            : templates.map(t => (
                                                <option key={t.id} value={String(t.id)}>
                                                    {t.name} ({t.year} · {t.gender === 'M' ? "Men's" : "Women's"})
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                            )}

                            <div className="sm:col-span-2">
                                <label className="block text-xs text-gray-400 mb-1">Tournament name</label>
                                <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                                    placeholder="NCAA Men's Tournament 2027"
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                                    required />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Year</label>
                                <input type="number" value={newYear} onChange={e => setNewYear(parseInt(e.target.value))}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                                    required />
                            </div>

                            {/* Gender — only shown in blank mode; template mode derives it from the chosen template */}
                            {createMode === 'blank' && (
                                <>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Total teams</label>
                                        <input type="number" value={newTotalTeams} onChange={e => setNewTotalTeams(parseInt(e.target.value))}
                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                                            required />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Gender</label>
                                        <select value={newGender} onChange={e => setNewGender(e.target.value)}
                                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                            <option value="M">Men's</option>
                                            <option value="W">Women's</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className={createMode === 'template' ? 'sm:col-span-2' : 'flex items-end'}>
                                <button
                                    type="submit"
                                    disabled={createMode === 'template' && (cloning || !selectedTemplate)}
                                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-2 rounded-lg transition-colors text-sm">
                                    {cloning
                                        ? 'Cloning…'
                                        : createMode === 'template'
                                            ? 'Clone tournament'
                                            : 'Create tournament'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                        <h3 className="text-lg font-black text-white mb-4">Existing tournaments</h3>
                        <div className="space-y-3">
                            {tournaments.map(t => (
                                <div key={t.id} className={'flex items-center justify-between p-4 rounded-lg border ' +
                                    (activeTournament?.id === t.id ? 'border-orange-500 bg-gray-800' : 'border-gray-800')}>
                                    {editTournId === t.id ? (
                                        <div className="flex-1 grid grid-cols-2 gap-2 mr-3">
                                            <input value={editTournData.name || ''} onChange={e => setEditTournData({...editTournData, name: e.target.value})}
                                                placeholder="Tournament name"
                                                className="col-span-2 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500" />
                                            <input type="number" value={editTournData.year || ''} onChange={e => setEditTournData({...editTournData, year: parseInt(e.target.value)})}
                                                placeholder="Year"
                                                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500" />
                                            <input type="number" value={editTournData.total_teams || ''} onChange={e => setEditTournData({...editTournData, total_teams: parseInt(e.target.value)})}
                                                placeholder="Total teams"
                                                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-orange-500" />
                                            {user?.is_platform_admin && (
                                                <>
                                                    <label className="col-span-2 flex items-center gap-2 text-xs text-gray-300 mt-1">
                                                        <input type="checkbox"
                                                            checked={!!editTournData.is_template}
                                                            onChange={e => setEditTournData({...editTournData, is_template: e.target.checked})}
                                                            className="rounded" />
                                                        Available as a clone template for other organizers
                                                    </label>
                                                    <label className="col-span-2 flex items-center gap-2 text-xs text-gray-300">
                                                        <input type="checkbox"
                                                            checked={!!editTournData.is_sync_source}
                                                            onChange={e => setEditTournData({...editTournData, is_sync_source: e.target.checked})}
                                                            className="rounded" />
                                                        Canonical source for results sync (only one per year + gender)
                                                    </label>
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-white font-bold text-sm">
                                                {t.name}
                                                {t.is_template && (
                                                    <span className="ml-2 bg-orange-900 text-orange-300 text-xs px-2 py-0.5 rounded-full align-middle">
                                                        Template
                                                    </span>
                                                )}
                                                {t.is_sync_source && (
                                                    <span className="ml-2 bg-blue-900 text-blue-300 text-xs px-2 py-0.5 rounded-full align-middle">
                                                        Sync source
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-gray-500 text-xs">{t.year} — {t.total_teams} teams — {t.gender === 'M' ? "Men's" : "Women's"}</p>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        {editTournId === t.id ? (
                                            <>
                                                <button onClick={() => handleSaveTournament(t.id)}
                                                    className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-2 py-1 rounded transition-colors">Save</button>
                                                <button onClick={() => setEditTournId(null)}
                                                    className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded transition-colors">Cancel</button>
                                            </>
                                        ) : (
                                            <button onClick={() => { setEditTournId(t.id); setEditTournData({ name: t.name, year: t.year, total_teams: t.total_teams, ...(user?.is_platform_admin ? { is_template: t.is_template, is_sync_source: t.is_sync_source } : {}) }); }}
                                                className="bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs px-2 py-1 rounded transition-colors">Edit</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {section === 'rounds' && (
                <div className="space-y-6">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                        <h3 className="text-lg font-black text-white mb-1">Active tournament</h3>
                        <p className="text-gray-400 text-sm mb-4">{activeTournament?.name || 'No tournament selected'}</p>

                        {rounds.length === 0 ? (
                            <div>
                                <p className="text-gray-500 text-sm mb-4">No rounds yet. Set deadlines and create them.</p>
                                <div className="space-y-3 mb-4">
                                    {roundInputs.map((r, i) => (
                                        <div key={r.round_number} className="flex items-center gap-3">
                                            <span className="text-gray-400 text-sm w-32">{r.round_name}</span>
                                            <input type="datetime-local"
                                                value={r.pick_deadline}
                                                onChange={e => {
                                                    const updated = [...roundInputs];
                                                    updated[i] = { ...updated[i], pick_deadline: e.target.value };
                                                    setRoundInputs(updated);
                                                }}
                                                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <button onClick={handleCreateRounds}
                                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm">
                                    Create rounds
                                </button>
                            </div>
                        ) : (
                            <div>
                                <p className="text-gray-500 text-xs mb-3">
                                    Pick group — rounds with the same group number are submitted together. Leave blank for individual submission.
                                </p>
                                <div className="space-y-3">
                                    {rounds.map(r => (
                                        <div key={r.id} className="bg-gray-800 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-white font-bold text-sm">{r.round_name}</span>
                                                {roundEdits[r.id]?.pick_group && (
                                                    <span className="text-xs text-blue-400 bg-blue-900 px-2 py-0.5 rounded-full">
                                                        Group {roundEdits[r.id].pick_group}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <input type="datetime-local"
                                                    value={roundEdits[r.id]?.pick_deadline || ''}
                                                    onChange={e => setRoundEdits(prev => ({
                                                        ...prev,
                                                        [r.id]: { ...prev[r.id], pick_deadline: e.target.value }
                                                    }))}
                                                    onBlur={() => handleUpdateDeadline(r)}
                                                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                                                />
                                                <div className="flex items-center gap-2">
                                                   <label className="text-xs text-gray-400">Group <span className="text-gray-600">(r-by-r only)</span></label>
                                                    <input type="number" min="1" max="9"
                                                        value={roundEdits[r.id]?.pick_group || ''}
                                                        onChange={e => setRoundEdits(prev => ({
                                                            ...prev,
                                                            [r.id]: { ...prev[r.id], pick_group: e.target.value }
                                                        }))}
                                                        onBlur={() => handleUpdatePickGroup(r)}
                                                        placeholder="—"
                                                        className="w-16 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 text-center"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminSeasonSetup;
